import _extends from "@babel/runtime/helpers/extends";
import { Spin, Form, Button, AutoComplete, Input, Alert } from "antd";
import { observer } from "mobx-react";
import { useState, useCallback, useMemo } from "react";
import { IMPREZA_BUTTON_PROPS, __ } from "../../../../utils";
import { RealCommerceError } from "../../../../exceptions";
import { useRealCommerceErrorHandling } from "../../../../hooks";
import { useStores } from "../../../../store";
import { copyToClipboard } from "@devowl-wp/react-utils";
const AccountAffiliateLinksFormLayout = {
  labelCol: {
    span: 6
  },
  wrapperCol: {
    span: 16
  }
};

/**
 * Extracted from https://vendors.paddle.com/affiliates with the following console script:
 *
 * ```js
 * const result = {};
 * jQuery(".pui-table tr").get()
 *     .map((tr) => [jQuery(tr).children(":eq(1)").text(), jQuery(tr).children(":eq(4)").children("a:first").attr("href")])
 *     .filter(([, href]) => href)
 *     .map(([name, link]) => {
 *         const lang = name.split(" ")[0];
 *         return [lang.replace(/[\(\)]/g, ""), name.substr(lang.length).trim(), link];
 *     })
 *     .forEach(([lang, name, link]) => {
 *         result[lang] = result[lang] || {};
 *         result[lang][name] = link;
 *     });
 * console.log(result);
 * ```
 *
 * TODO should this be hardcoded?!
 */
const PREDEFINED_LINK_LIST = {
  DE: {
    "devowl.io Startseite": "/de/",
    "Real Cookie Banner": "/de/wordpress-real-cookie-banner/",
    "Real Cookie Banner vs. alternative WordPress Cookie Plugins": "/de/wordpress-real-cookie-banner/vergleichen/",
    "Real Cookie Banner: Entwickler API": "/de/wordpress-real-cookie-banner/entwickler-api/",
    "Real Category Management": "/de/wordpress-real-category-management/",
    "Real Media Library": "/de/wordpress-real-media-library/",
    "Real Physical Media": "/de/wordpress-real-physical-media/",
    "Real Thumbnail Generator": "/de/wordpress-real-thumbnail-generator/"
  },
  EN: {
    "devowl.io frontpage": "/",
    "Real Cookie Banner": "/wordpress-real-cookie-banner/",
    "Real Cookie Banner vs. alternative WordPress Cookie Plugins": "/wordpress-real-cookie-banner/compare/",
    "Real Cookie Banner: Developer API": "/wordpress-real-cookie-banner/developer-api/",
    "Real Category Management": "/wordpress-real-category-management/",
    "Real Media Library": "/wordpress-real-media-library/",
    "Real Physical Media": "/wordpress-real-physical-media/",
    "Real Thumbnail Generator": "/wordpress-real-thumbnail-generator/"
  }
};
const PREDEFINED_LINK_DOMAIN = "https://devowl.io";
const AccountAffiliateLinksForm = observer(() => {
  const [targetLinkTerm, setTargetLinkTerm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(undefined);
  const initialValues = {
    target: ""
  };
  const [form] = Form.useForm();
  const handleRealCommerceError = useRealCommerceErrorHandling();
  const {
    accountStore
  } = useStores();
  const {
    affiliateManufacturer,
    session: {
      affiliate
    }
  } = accountStore;
  const onFinish = useCallback(async _ref => {
    let {
      target
    } = _ref;
    setBusy(true);
    try {
      const result = await affiliate.createLink(target);
      setResult(result);
      form.resetFields();
      copyToClipboard(result.affiliateLink);
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
    } finally {
      setBusy(false);
    }
  }, [affiliate]);
  const autocompleteOptions = useMemo(() => Object.keys(PREDEFINED_LINK_LIST).reduce((result, lang) => {
    result.push({
      label: lang === "DE" ? __("German") : lang === "EN" ? __("English") : lang,
      options: Object.keys(PREDEFINED_LINK_LIST[lang]).map(label => ({
        key: `${lang}${label}`,
        value: `${PREDEFINED_LINK_DOMAIN}${PREDEFINED_LINK_LIST[lang][label]}`,
        label
      })).filter(_ref2 => {
        let {
          label,
          value
        } = _ref2;
        return targetLinkTerm ? label.toLowerCase().indexOf(targetLinkTerm.toLowerCase()) > -1 || value.toLowerCase().indexOf(targetLinkTerm.toLowerCase()) > -1 : true;
      })
    });
    return result;
  }, []).filter(_ref3 => {
    let {
      options: {
        length
      }
    } = _ref3;
    return length;
  }), [targetLinkTerm]);
  return /*#__PURE__*/React.createElement("div", {
    className: "real-commerce-affiliate-link-form"
  }, /*#__PURE__*/React.createElement(Spin, {
    spinning: busy
  }, /*#__PURE__*/React.createElement("p", null, __("Create unique affiliate links that redirect potential customers to %s to earn commissions on referred sales.", affiliateManufacturer.name.replace("GmbH", ""))), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Form, _extends({
    form: form,
    onFinish: onFinish,
    initialValues: initialValues,
    colon: false,
    labelWrap: true
  }, AccountAffiliateLinksFormLayout), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("Target link"),
    extra: __("Enter any URL on devowl.io or choose one from our predefined list."),
    required: true,
    name: "target",
    rules: [{
      required: true,
      type: "url",
      message: __("Please provide a valid URL!")
    }]
  }, /*#__PURE__*/React.createElement(AutoComplete, {
    options: autocompleteOptions,
    onSearch: setTargetLinkTerm,
    placeholder: __("e.g. https://devowl.io/wordpress-real-cookie-banner/")
  })), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("Affiliate link"),
    extra: result ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Alert, {
      type: "success",
      showIcon: true,
      message: __("Link successfully created and copied to your clipboard!"),
      style: {
        marginTop: 10
      }
    }), /*#__PURE__*/React.createElement(Alert, {
      type: "warning",
      showIcon: true,
      description: __("You may know it from other affiliate systems that you just append a certain parameter to the URL. Unfortunately, this is not possible with our system, as we use a slightly different type of tracking to offer cookie-less tracking and anti-ad-blocker mechanisms. Please use only the URL generated above that refered sale get tracked."),
      message: __("Can I append a parameter directly to an URL instead of using the generated URL?"),
      style: {
        marginTop: 10
      }
    })) : __('Click on "Generate link" to generate your unique affiliate link...')
  }, /*#__PURE__*/React.createElement(Input, {
    disabled: !result,
    readOnly: !!result,
    value: result ? result.affiliateLink : ""
  })), /*#__PURE__*/React.createElement(Form.Item, {
    wrapperCol: {
      offset: AccountAffiliateLinksFormLayout.labelCol.span
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({
    type: "primary",
    size: "large",
    htmlType: "submit"
  }, IMPREZA_BUTTON_PROPS), __("Generate link"))))));
});
export { AccountAffiliateLinksForm, AccountAffiliateLinksFormLayout };